import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';

const ZoominAppLogo = ({ projectId, imgSrc, title, isPremium, index = 0 }) => {
    const { theme, themes } = useContext(ThemeContext);
    const isLight = themes[theme]?.isIconDark;
    const ledGlowColor = themes[theme]?.glowColor || 'rgba(56, 189, 248, 0.85)';

    return (
        <div
            className="animate-spring-up relative z-0 flex flex-col items-center justify-start w-36 sm:w-40"
            style={{
                animationDelay: `${index * 0.15}s`,
            }}
        >
            <Link 
                to={`/projects/${projectId}`} 
                className="group flex flex-col items-center text-center no-underline focus:outline-none w-full"
            >
                {/* Isolated Icon + Glow Container: Zooms fast and smoothly on hover */}
                <motion.div 
                    className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-[22%] p-0.5"
                    whileHover={{ scale: 1.09 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 22 }}
                >
                    {/* Crisp Core LED Strip: lower group-hover:opacity-100 down to 60-70% */}
                    <div 
                        className="absolute -inset-1 rounded-[24%] opacity-0 group-hover:opacity-65 blur-sm transition-opacity duration-150 pointer-events-none"
                        style={{ backgroundColor: ledGlowColor }}
                    />

                    {/* Secondary Rim Light: lower group-hover:opacity-75 down to 35-45% */}
                    <div 
                        className="absolute -inset-2 rounded-[26%] opacity-0 group-hover:opacity-40 blur-md transition-opacity duration-150 pointer-events-none"
                        style={{ backgroundColor: ledGlowColor }}
                    />

                    {/* App Icon */}
                    <img
                        src={imgSrc}
                        alt={title}
                        decoding="async"
                        className={`relative z-10 w-full h-full object-cover rounded-[22%] shadow-md transition-colors duration-150 ${
                            isLight
                                ? 'border border-black/10 group-hover:border-black/30'
                                : 'border border-white/10 group-hover:border-white/50'
                        }`}
                    />
                </motion.div>

                {/* App Title (Completely stationary) */}
                <h3 className={`mt-3 text-sm sm:text-base font-semibold tracking-tight transition-colors duration-150 line-clamp-1 group-hover:opacity-100 ${themes[theme].text}`}>
                    {title}
                </h3>

                {/* Tag / Premium Pill (Completely stationary) */}
                {isPremium ? (
                    <span 
                        className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide border ${
                            isLight
                                ? 'bg-amber-500/15 text-amber-700 border-amber-500/30'
                                : 'bg-amber-400/10 text-amber-300 border-amber-400/25'
                        }`}
                    >
                        <FontAwesomeIcon icon={faStar} className="text-[8px]" />
                        Featured
                    </span>
                ) : (
                    <span className={`mt-1 text-[11px] font-mono tracking-wide ${themes[theme].subText} opacity-70`}>
                        Utility
                    </span>
                )}
            </Link>
        </div>
    );
};

export default ZoominAppLogo;